import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, styled, TextField } from "@mui/material";
import DefaultButton from "./UI/DefaultButton";
import InvalidSummaryMessage from "./UI/InvalidSummaryMessage";
import { SessionActionStatus } from "../context/sessionStore";
import useSessionStore from "../context/useSessionStore";
import { useEventReportedContext } from "../analytics/EventReporterContext";
import { MIX_PANEL_EVENTS } from "../analytics/eventReporter";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { navigateWithLocale } from "../utils";
import { useNavigate } from "react-router-dom";

//region [[ Styles ]]

const PatientSummaryFooter = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: 10,
  justifyContent: "space-between",
  height: 34,
  "& .done-button, .edit-button": {
    width: 100,
  },
  "& .done-button": {
    marginLeft: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  [theme.breakpoints.down("sm")]: {
    justifyContent: "start",
    flexDirection: "column",
    height: "auto",
  },
}));

const PatientLoading = styled("div")({
  marginTop: 20,
  textAlign: "center",
  height: 34,
});

//endregion [[ Styles ]]
//region [[ Props ]]
//endregion [[ Props ]]
//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientSummary = () => {
  const [value, setValue] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [invalid, setInvalid] = useState<boolean>(false);

  const { sessionId, patientSummary, addSession, ragSettings, sessionUser } =
    useSessionStore();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const reporter = useEventReportedContext();
  const { translate, locale } = useTranslateByGender();
  function highlightSummary(element: HTMLElement = document.body) {
    if (!element) return;
    element.scrollIntoView({
      behavior: "auto",
      block: "center",
    });
    if (
      !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      )
    ) {
      element.focus();
    }
  }

  useEffect(() => {
    if (patientSummary?.text) {
      setValue(patientSummary.text);
      setSubmitted(true);
      setEditMode(false);
      setInvalid(false);
    } else {
      setValue("");
      setSubmitted(false);
      setEditMode(true);
    }
  }, [patientSummary]);

  useEffect(() => {
    if (editMode) {
      highlightSummary(inputRef.current);
    }
  }, [editMode]);

  const handleAddPatientSession = async () => {
    if (value !== patientSummary?.text || ragSettings.debugMode) {
      const addedSummary = await addSession(
        value,
        sessionId,
        ragSettings.generateSuggestions,
      );
      reporter?.reportEvent(
        sessionId
          ? MIX_PANEL_EVENTS.SUMMARY_EDITED
          : MIX_PANEL_EVENTS.SUMMARY_SUBMITTED,
        {
          sessionId,
          failed: addedSummary.status !== SessionActionStatus.success,
        },
      );
      setInvalid(addedSummary.status === SessionActionStatus.invalid);
      if (addedSummary.status === SessionActionStatus.success) {
        setEditMode(false);
        if (sessionUser?.userName)
          navigate(navigateWithLocale("../" + addedSummary.sessionId, locale)); //only navigate if user is logged in
      }
    } else {
      setEditMode(false);
    }
  };

  return (
    <>
      <TextField
        fullWidth
        id={"patientSummary"}
        value={value}
        placeholder={translate("DIALOGUE_SUMMARY_PLACEHOLDER")}
        onChange={(e) => setValue(e.target.value)}
        disabled={!editMode}
        multiline
        minRows={8}
        inputRef={inputRef}
      />
      {invalid && <InvalidSummaryMessage />}

      <PatientSummaryFooter>
        {editMode && submitted && (
          <DefaultButton
            onClick={() => setEditMode(false)}
            className="alignEnd"
          >
            {translate("CANCEL")}
          </DefaultButton>
        )}
        {editMode ? (
          <DefaultButton
            onClick={handleAddPatientSession}
            disabled={value.length < 20}
            className={`alignEnd ${submitted ? "" : "done-button"}`}
          >
            {submitted ? translate("UPDATE_ASSESSMENT") : translate("DONE")}
          </DefaultButton>
        ) : (
          <DefaultButton
            onClick={() => setEditMode(true)}
            className="edit-button"
          >
            {translate("EDIT")}
          </DefaultButton>
        )}
      </PatientSummaryFooter>
    </>
  );
};
